// src/components/PriceSimulator/PriceSimulator.tsx
import { formatMoney } from 'helpers/numberHelper';
import React, { useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
// import { formatMoney } from '../../utils/numberHelper'; // Adjust the path as needed

interface PriceSimulatorProps {
  minStudents?: number;
  maxStudents?: number;
  defaultStudents?: number;
  taxRate?: number; 
  className?: string;
}

const PriceSimulator: React.FC<PriceSimulatorProps> = ({
  minStudents = 100,
  maxStudents = 2000,
  defaultStudents = 600,
  taxRate = 20, 
  className = ''
}) => {
  const { t, i18n } = useTranslation();
  const currentLanguage = i18n.language;
  const [studentCount, setStudentCount] = useState<number>(defaultStudents);
  const [inputValue, setInputValue] = useState<string>(defaultStudents.toString());
  const [billingType, setBillingType] = useState<'monthly' | 'annual'>('monthly');

  // Price tiers configuration
  const tiers = [
    { cap: 250, included: 100, base: 539, extraRate: 2.5 },
    { cap: 600, included: 250, base: 914, extraRate: 1.5 },
    { cap: Infinity, included: 600, base: 1439, extraRate: 1 }
  ];

  // Format number based on language
  const formatNumber = (num: number): string => {
    return num.toLocaleString(currentLanguage === 'ar' ? 'ar-MA' : 'fr-MA');
  };

  useEffect(() => {
    setInputValue(studentCount.toString());
  }, [studentCount]);

  // Calculate the price based on student count and billing type
  const calculatePrice = useCallback(() => {
    const n = Math.max(minStudents, Math.min(studentCount, maxStudents));
    const tier = tiers.find(t => n <= t.cap)!;
    const extra = Math.max(0, n - tier.included);
    const monthlyPriceHT = tier.base + extra * tier.extraRate;
    
    // Calculate TTC (including tax)
    const monthlyPriceTTC = monthlyPriceHT * (1 + taxRate / 100);

    if (billingType === 'annual') {
      const annualHT = monthlyPriceHT * 10 * 0.85; // 10 months with 15% discount
      const annualTTC = annualHT * (1 + taxRate / 100);
      
      return {
        priceHT: Math.round(annualHT),
        priceTTC: Math.round(annualTTC),
        periodLabel: t('priceSimulator.perYear'),
        formattedPriceHT: formatMoney(Math.round(annualHT)),
        formattedPriceTTC: formatMoney(Math.round(annualTTC)),
        monthlyEquivalentHT: formatMoney(Math.round(monthlyPriceHT)),
        monthlyEquivalentTTC: formatMoney(Math.round(monthlyPriceTTC)),
        rawPriceHT: Math.round(annualHT),
        rawPriceTTC: Math.round(annualTTC),
        rawMonthlyHT: monthlyPriceHT,
        rawMonthlyTTC: monthlyPriceTTC
      };
    } else {
      return {
        priceHT: monthlyPriceHT,
        priceTTC: monthlyPriceTTC,
        periodLabel: t('priceSimulator.perMonth'),
        formattedPriceHT: formatMoney(monthlyPriceHT),
        formattedPriceTTC: formatMoney(monthlyPriceTTC),
        monthlyEquivalentHT: null,
        monthlyEquivalentTTC: null,
        rawPriceHT: monthlyPriceHT,
        rawPriceTTC: monthlyPriceTTC,
        rawMonthlyHT: monthlyPriceHT,
        rawMonthlyTTC: monthlyPriceTTC
      };
    }
  }, [studentCount, billingType, minStudents, maxStudents, taxRate, t]);

  const handleSliderChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(event.target.value, 10);
    setStudentCount(value);
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setInputValue(value);
    
    const numValue = parseInt(value, 10);
    if (!isNaN(numValue) && numValue >= minStudents && numValue <= maxStudents) {
      setStudentCount(numValue);
    }
  };

  const handleInputBlur = () => {
    const numValue = parseInt(inputValue, 10);
    if (isNaN(numValue)) {
      setInputValue(studentCount.toString());
    } else {
      const clampedValue = Math.max(minStudents, Math.min(numValue, maxStudents));
      setStudentCount(clampedValue);
    }
  };

  const handleBillingChange = (type: 'monthly' | 'annual') => {
    setBillingType(type);
  };

  const calculatedPrice = calculatePrice();
  const sliderPercentage = ((studentCount - minStudents) / (maxStudents - minStudents)) * 100;

  return (
    <div className={`card shadow-sm border-0 ${className}`} dir={currentLanguage === 'ar' ? 'rtl' : 'ltr'}>
      <div className="card-body p-4 p-md-5">
        <div className="text-center mb-4">
          <h3 className="fw-bold text-primary mb-2">
            {t('priceSimulator.title')}
          </h3>
          <p className="text-muted mb-0">
            {t('priceSimulator.subtitle')}
          </p>
        </div>

        {/* Student Count Display with Input */}
        <div className="mb-4">
          <label className="form-label fw-semibold d-flex justify-content-between align-items-center">
            <span className="text-primary">
              <i className={`fas fa-users ${currentLanguage === 'ar' ? 'ms-2' : 'me-2'}`}></i>
              {t('priceSimulator.studentsCount')}
            </span>
            <div className="d-flex align-items-center">
              <span className="badge bg-primary rounded-pill px-3 py-2 me-2">
                {formatNumber(studentCount)}
              </span>
              <div className="input-group input-group-sm" style={{ width: '120px' }}>
                <input
                  type="number"
                  className="form-control form-control-sm text-center"
                  min={minStudents}
                  max={maxStudents}
                  value={inputValue}
                  onChange={handleInputChange}
                  onBlur={handleInputBlur}
                  aria-label="Student count"
                />
                <span className="input-group-text">{t('students')}</span>
              </div>
            </div>
          </label>

          {/* Range Slider */}
          <input
            type="range"
            className="form-range"
            min={minStudents}
            max={maxStudents}
            step="1"
            value={studentCount}
            onChange={handleSliderChange}
            style={{
              '--bs-range-thumb-bg': '#533B9A',
              '--bs-range-track-bg': '#e0e0e0',
              '--bs-range-progress': `${sliderPercentage}%`,
            } as React.CSSProperties}
          />

          <div className="d-flex justify-content-between mt-1 text-muted small">
            <span>{formatNumber(minStudents)}</span>
            <span>{formatNumber(maxStudents)}</span>
          </div>
        </div>

        {/* Billing Type Selection */}
        <div className="mb-4">
          <label className="form-label fw-semibold">
            <i className={`fas fa-calendar-alt ${currentLanguage === 'ar' ? 'ms-2' : 'me-2'}`}></i>
            {t('priceSimulator.billingPeriod')}
          </label>
          <div className="btn-group w-100" role="group">
            <button
              type="button"
              className={`btn ${billingType === 'monthly' ? 'btn-primary' : 'btn-outline-primary'} py-2`}
              onClick={() => handleBillingChange('monthly')}
            >
              <i className={`fas fa-calendar-day ${currentLanguage === 'ar' ? 'ms-2' : 'me-2'}`}></i>
              {t('priceSimulator.monthly')}
            </button>
            <button
              type="button"
              className={`btn ${billingType === 'annual' ? 'btn-primary' : 'btn-outline-primary'} py-2`}
              onClick={() => handleBillingChange('annual')}
            >
              <i className={`fas fa-calendar-year ${currentLanguage === 'ar' ? 'ms-2' : 'me-2'}`}></i>
              {t('priceSimulator.annual')}
              <span className={`badge bg-success ${currentLanguage === 'ar' ? 'me-1' : 'ms-1'}`}>
                -15%
              </span>
            </button>
          </div>
        </div>

        {/* Price Result */}
        <div className="card border-primary mb-3">
          <div className="card-body text-center py-4">
            <div className="mb-2">
              <span className="text-muted">
                {t('priceSimulator.estimatedPrice')}
              </span>
            </div>
            
            {/* HT Price */}
            <div className="mb-2">
              <span className="text-muted small">HT</span>
              <div className="display-6 fw-bold text-primary">
                {calculatedPrice.formattedPriceHT}
              </div>
              <div className="text-muted">
                <small>{calculatedPrice.periodLabel}</small>
              </div>
            </div>

            {/* TTC Price */}
            <div className="border-top pt-2 mt-2">
              <span className="text-muted small">TTC (TVA {taxRate}%)</span>
              <div className="display-4 fw-bold text-primary">
                {calculatedPrice.formattedPriceTTC}
              </div>
              <div className="text-muted">
                <small>{calculatedPrice.periodLabel}</small>
                {calculatedPrice.monthlyEquivalentTTC && billingType === 'annual' && (
                  <div className="mt-1 small">
                    <i className={`fas fa-info-circle ${currentLanguage === 'ar' ? 'ms-1' : 'me-1'}`}></i>
                    {t('priceSimulator.equivalentTo')} {calculatedPrice.monthlyEquivalentTTC}
                    {t('priceSimulator.perMonthShort')} TTC
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Pricing Tiers Info */}
        <div className="accordion" id="pricingInfoAccordion">
          <div className="accordion-item">
            <h2 className="accordion-header">
              <button
                className="accordion-button collapsed"
                type="button"
                data-bs-toggle="collapse"
                data-bs-target="#pricingDetails"
              >
                <i className={`fas fa-info-circle ${currentLanguage === 'ar' ? 'ms-2' : 'me-2'}`}></i>
                {t('priceSimulator.pricingDetails')}
              </button>
            </h2>
            <div id="pricingDetails" className="accordion-collapse collapse" data-bs-parent="#pricingInfoAccordion">
              <div className="accordion-body">
                <div className="table-responsive">
                  <table className="table table-sm">
                    <thead>
                      <tr>
                        <th>{t('priceSimulator.upTo')}</th>
                        <th>{t('priceSimulator.baseIncluded')}</th>
                        <th>{t('priceSimulator.basePrice')}</th>
                        {/* <th>{t('priceSimulator.basePrice')} TTC</th> */}
                        <th>{t('priceSimulator.extraPerStudent')}</th>
                        {/* <th>{t('priceSimulator.extraPerStudent')} TTC</th> */}
                      </tr>
                    </thead>
                    <tbody>
                      {tiers.map((tier, index) => (
                        <tr key={index} className={studentCount <= tier.cap ? 'table-primary' : ''}>
                          <td>{tier.cap === Infinity ? '∞' : formatNumber(tier.cap)}</td>
                          <td>{formatNumber(tier.included)}</td>
                          <td>{formatMoney(tier.base)}</td>
                          {/* <td>{formatMoney(tier.base * (1 + taxRate / 100))}</td> */}
                          <td>{formatMoney(tier.extraRate)}</td>
                          {/* <td>{formatMoney(tier.extraRate * (1 + taxRate / 100))}</td> */}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
};

export default PriceSimulator;